import {
  addToast,
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import { AzureFilesUploaderControlled } from '../../../components/AzureFilesUploader/AzureFileUploaderControlled';
import {
  FileRequiredTypes,
  SecureResponseFile,
} from '../../../components/AzureFilesUploader/types';
import { useActionKitImp } from '../../../components/ozmoit/common';
import { TranslationsKeys } from '../../../locales/translations';
import { recordRequirementResultAction } from '../actions';
import { useRequirementStudentsLoader } from '../loaders/RequirementStudentsLoader';
import {
  ElectiveRequirementItem,
  ElectiveRequirementStudentRow,
} from '../types';

export type RequirementResultModalRef = {
  openUpdate: (row: ElectiveRequirementStudentRow) => void;
  openDetail: (row: ElectiveRequirementStudentRow) => void;
};

type FormValues = {
  title?: string;
  description?: string;
  date?: string;
  result?: SelectOptionType | null;
  grade?: string;
  attachment?: SecureResponseFile | null;
};

const TITLE_MAX = 50;
const DESCRIPTION_MAX = 100;

const RequirementResultModal = (
  { item }: { item: ElectiveRequirementItem },
  ref: Ref<RequirementResultModalRef>,
) => {
  const { t } = useTranslation(TranslationsKeys.GRADUATION_REQUIREMENTS_REVIEW);
  const { refresh } = useRequirementStudentsLoader();
  const [open, setOpen] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [row, setRow] = useState<ElectiveRequirementStudentRow | null>(null);

  const methods = useForm<FormValues>();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const titleValue = useWatch({ control, name: 'title' }) ?? '';
  const descriptionValue = useWatch({ control, name: 'description' }) ?? '';

  const resultOptions: SelectOptionType[] = [
    { value: 'approved', label: t('modal.result.approved') },
    { value: 'rejected', label: t('modal.result.rejected') },
  ];

  const fillForm = useCallback(
    (target: ElectiveRequirementStudentRow) => {
      reset({
        title: target.requirementTitle ?? '',
        description: target.requirementDescription ?? '',
        date: target.renditionDate ?? undefined,
        result:
          target.status === 'pending'
            ? null
            : (resultOptions.find((o) => o.value === target.status) ?? null),
        grade: target.requirementGrade ?? '',
        attachment: null,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reset, t],
  );

  useImperativeHandle(ref, () => ({
    openUpdate: (target) => {
      setRow(target);
      setReadOnly(false);
      fillForm(target);
      setOpen(true);
    },
    openDetail: (target) => {
      setRow(target);
      setReadOnly(true);
      fillForm(target);
      setOpen(true);
    },
  }));

  const close = useCallback(() => {
    setOpen(false);
    setRow(null);
    reset({});
  }, [reset]);

  const { action: save } = useActionKitImp(recordRequirementResultAction, {
    onSuccess: () => {
      addToast({ color: 'success', text: t('modal.saveSuccess') });
      close();
      refresh();
    },
    onError: {
      unknown: () => {
        addToast({ color: 'danger', text: t('modal.saveError') });
      },
    },
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!row) {
        return;
      }
      await save({
        typeId: item.typeId,
        studyPlanEnrollmentId: row.studyPlanEnrollmentId,
        title: (values.title ?? '').trim(),
        description: (values.description ?? '').trim(),
        date: values.date as string,
        approved: values.result?.value === 'approved',
        grade:
          item.allowGrades && values.grade
            ? Number(values.grade.replace(',', '.'))
            : undefined,
        fileId: values.attachment?.id ?? null,
      });
    },
    [row, save, item.typeId, item.allowGrades],
  );

  return (
    <Modal isOpen={open} toggle={close} size="md">
      <Container fluid>
        <h2 className="text-center text-dark fs-20 mt-3 mb-4">
          {readOnly ? t('modal.titleDetail') : t('modal.titleUpdate')}
        </h2>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <TextOutlinedInput
                name="title"
                control={control}
                label={item.name}
                disabled={readOnly}
                maxLength={TITLE_MAX}
                rules={{
                  required: t('modal.validation.required') as string,
                  maxLength: {
                    value: TITLE_MAX,
                    message: t('modal.validation.maxTitle'),
                  },
                }}
              />
              {!readOnly && (
                <small className="text-muted d-block text-right">
                  {titleValue.length}/{TITLE_MAX}
                </small>
              )}
            </div>
            <div className={readOnly ? 'mb-3' : 'mb-1'}>
              <TextAreaInput
                name="description"
                control={control}
                label={t('modal.fields.description')}
                disabled={readOnly}
                maxLength={DESCRIPTION_MAX}
                rules={{
                  required: t('modal.validation.required') as string,
                  maxLength: {
                    value: DESCRIPTION_MAX,
                    message: t('modal.validation.maxDescription'),
                  },
                }}
              />
              {!readOnly && (
                <small className="text-muted d-block text-right">
                  {descriptionValue.length}/{DESCRIPTION_MAX}
                </small>
              )}
            </div>
            <Row>
              <Col xs={12} md={6}>
                <DateInput
                  name="date"
                  control={control}
                  label={t('modal.fields.date')}
                  disabled={readOnly}
                />
              </Col>
              <Col xs={12} md={6}>
                <Select
                  name="result"
                  control={control}
                  label={t('modal.fields.result')}
                  disabled={readOnly}
                  options={resultOptions}
                  isClearable={false}
                />
              </Col>
            </Row>
            {item.allowGrades && (
              <Row>
                <Col xs={12} md={6}>
                  <TextInput
                    name="grade"
                    control={control}
                    label={t('modal.fields.grade')}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
            )}
            <div className="my-2">
              <label className="text-muted fs-14 d-block">
                {t('modal.fields.attachment')}
              </label>
              {!readOnly ? (
                <AzureFilesUploaderControlled
                  control={control}
                  name="attachment"
                  fileType={FileRequiredTypes.DegreeRequirementAttachment}
                />
              ) : (
                row?.detailUrl && (
                  <a href={row.detailUrl} target="_blank" rel="noreferrer">
                    {t('table.headers.detail')}
                  </a>
                )
              )}
            </div>
            <Row className="pt-1">
              {readOnly ? (
                <Col xs={12}>
                  <Button
                    fullwidth
                    outlined
                    text={t('modal.buttons.close')}
                    onClick={close}
                  />
                </Col>
              ) : (
                <>
                  <Col xs={12} md={6}>
                    <Button
                      fullwidth
                      outlined
                      text={t('modal.buttons.close')}
                      onClick={close}
                      className="mb-2"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Button
                      fullwidth
                      type="submit"
                      text={t('modal.buttons.submit')}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="mb-2"
                    />
                  </Col>
                </>
              )}
            </Row>
          </form>
        </FormProvider>
      </Container>
    </Modal>
  );
};

export default forwardRef(RequirementResultModal);
